/**
 * Who owns the iOS audio session: a voice conversation or project sound
 * (sound-and-music design §2). Project sound on the share page and in the
 * editor must play with the ringer switch off, and WebKit's default category
 * (ambient) goes silent under it.
 *
 * One arbiter per platform, module-level, because every component builds its
 * own adapter and the session is one per app:
 * - A live voice session wins. Sound requested during one waits for it.
 * - When voice ends, the session goes back to playback if sound is still
 *   requested, and is released otherwise.
 * - Requesting or releasing twice is a no-op.
 *
 * Callers request on the first sound that plays, never on mount: playback
 * mixes with other audio but still takes the session, and a share page that
 * merely opened has no business with it.
 *
 * No `@capacitor/*` here: the web adapter uses this too.
 */

export interface AudioSessionBackend {
  voiceOn(): Promise<void>;
  voiceOff(): Promise<void>;
  playbackOn(): Promise<void>;
  playbackOff(): Promise<void>;
}

export interface AudioSessionArbiter {
  activateVoice(): Promise<void>;
  deactivateVoice(): Promise<void>;
  requestSound(): Promise<void>;
  releaseSound(): Promise<void>;
}

export function createAudioSessionArbiter(backend: AudioSessionBackend): AudioSessionArbiter {
  let voice = false;
  let sound = false;
  return {
    async activateVoice() {
      voice = true;
      await backend.voiceOn();
    },
    async deactivateVoice() {
      voice = false;
      await (sound ? backend.playbackOn() : backend.voiceOff());
    },
    async requestSound() {
      if (sound) return;
      sound = true;
      if (!voice) await backend.playbackOn();
    },
    async releaseSound() {
      if (!sound) return;
      sound = false;
      if (!voice) await backend.playbackOff();
    },
  };
}

/** WebKit's Audio Session API (Safari 17+), absent elsewhere. */
type AudioSessionType = "auto" | "playback" | "transient" | "transient-solo" | "ambient" | "play-and-record";
interface NavigatorAudioSession {
  audioSession?: { type: AudioSessionType };
}

function setWebAudioSessionType(type: AudioSessionType): void {
  try {
    const session = (navigator as Navigator & NavigatorAudioSession).audioSession;
    if (session) session.type = type;
  } catch { /* never throws */ }
}

/**
 * The browser's side: `navigator.audioSession.type`. "playback" plays through
 * the ringer switch; "auto" hands the choice back to the browser, which is
 * also right during a voice session, when capture decides it.
 */
export const webAudioSessionBackend: AudioSessionBackend = {
  async voiceOn() { setWebAudioSessionType("auto"); },
  async voiceOff() {},
  async playbackOn() { setWebAudioSessionType("playback"); },
  async playbackOff() { setWebAudioSessionType("auto"); },
};

/**
 * The page's one browser arbiter: the web adapter's voice and the project sound host share
 * it, so a voice session on the page still wins over sound.
 */
export const webAudioSession: AudioSessionArbiter = createAudioSessionArbiter(webAudioSessionBackend);

/**
 * The host's side of the browser session, for a page with no platform adapter: the prod
 * share island (`web/`), whose import graph cannot take `@capacitor/core`. Without it the
 * page stays on WebKit's default category and the iPhone's silent switch mutes the project
 * (the Warnock share link, 2026-10-26). Same shape as the host's `PlaybackSession`.
 */
export const webSoundPlayback = {
  requestSoundPlayback: (): Promise<void> => webAudioSession.requestSound(),
  releaseSoundPlayback: (): Promise<void> => webAudioSession.releaseSound(),
};
